/* eslint-disable react/jsx-handler-names */
import React, { useMemo, useState } from "react";
import Typography from "../../../components/atoms/typography";
import { constants, labelStyle } from "../../../constants/pageConstants/pdpConstants";
import get from "lodash.get";
import PropTypes from "prop-types";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import { COUNTRIES } from "../../../constants/common/common";
import { LAST_VISITED_ADDRESS, LAST_VISITED_PINCODE } from "../../../constants/storage";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { shallowEqual, useSelector } from "react-redux";
import useAnalytics from "../../../analytics/useAnalytics";
import Accordion from "../../../components/molecules/accordion";
import AccordionContent from "../../../components/molecules/accordion/accordionContent";
import AccordionTitle from "../../../components/molecules/accordion/accordionTitle";
import { InfoIcon, NotepadIcon } from "@phosphor-icons/react";
import CustomImage from "../../../components/atoms/customImage";

/**
 * Component that represents a section of the accordion, displaying content based on the provided server data.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered AccordionSection component.
 */
const AccordionSection = ({ currentItem, isMobile }) => {
  const { trackEvent } = useAnalytics();
  const [activeIndex, setActiveIndex] = useState(-1);
  const isInternational = currentItem?.catalogId !== COUNTRIES.INDIA;
  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const lastVisitedAddress = CookieUtil.getCookie(LAST_VISITED_ADDRESS);

  const { pincode, address, selectedIntLocation } = useSelector(
    (state) => ({
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      address: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock?.selectedIntLocation,
    }),
    shallowEqual,
  );

  const accordionItems = useMemo(() => {
    const content = currentItem?.productContent;

    if (!content) {
      return [];
    }

    return [
      {
        label: constants.PRODUCT_DESCRIPTION,
        html: content.productDescription,
      },
      {
        label: constants.CARE_INSTRUCTIONS,
        html: content.careInstructions,
      },
      {
        label: constants.DELIVERY_INFORMATION,
        html: content.deliveryInfo,
      },
    ].filter((item) => item.html);
  }, [currentItem?.productContent]);

  /**
   * Tracks the click event on the accordion title.
   * @param {*} labelName
   */
  const onTriggerClickEvent = (labelName) => {
    const eventObjectProperties = {
      page_name: currentItem?.urlIdentifier,
      page_type: cleverTapCommonWords.PDP,
      product_name: currentItem?.productName,
      pid: currentItem?.productId,
      sku_id: currentItem?.skuCode,
      description_type: labelName,
      product_price: currentItem?.productPrice?.price,
      city: isInternational ? selectedIntLocation?.location : null,
      area: isInternational ? null : address || lastVisitedAddress,
      pincode: isInternational ? null : pincode || lastVisitedPincode,
      system_pincode: isInternational ? null : pincode || lastVisitedPincode,
      flow: "new",
    };
    trackEvent(cleverTapCommonEvents.descriptionViewed, eventObjectProperties, ["clevertap"]);
  };

  /**
   * Handles accordion open for analytics. Deliberately does not scroll the page -
   * expanding/collapsing an accordion should only affect its own content.
   * @param {number} index
   */
  const onAccordionOpen = (index) => {
    const item = accordionItems[index];
    if (!item) return;

    onTriggerClickEvent(item.label);
  };

  /**
   * Returns the appropriate icon based on the label.
   * @param {string} label - The label of the accordion item.
   * @returns {JSX.Element} The icon element.
   */
  const getAccordionIcon = (label) => {
    if (label === constants.PRODUCT_DESCRIPTION) {
      return <InfoIcon size={isMobile ? 20 : 24} />;
    }
    if (label === constants.CARE_INSTRUCTIONS) {
      return <NotepadIcon size={isMobile ? 20 : 24} />;
    }
    if (label === constants.DELIVERY_INFORMATION) {
      return (
        <CustomImage
          src="/icons/ic-bike-gift.svg"
          alt="Instant Delivery"
          width={isMobile ? 20 : 24}
          height={isMobile ? 20 : 24}
        />
      );
    }
  };

  return (
    <div id="productDescriptionBox">
      <div className="flex w-full justify-between">
        <Typography variant="p" Tag="p" classes={`${labelStyle} xxl:text-18 w-full`}>
          {constants.ABOUT_THE_PRODUCT}
        </Typography>
        {!isMobile && (
          <div className="flex w-full text-right">
            <Typography
              id="product-sku-code"
              Tag={"p"}
              variant="p"
              classes="text-12 font-400 text-grey-400 basis-1/3 flex-grow"
            >
              {get(currentItem, "skuCode") || ""}
            </Typography>
          </div>
        )}
      </div>

      <Accordion>
        {accordionItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <AccordionTitle
              activeIndex={activeIndex}
              changeActiveIndex={setActiveIndex}
              index={index}
              onClickCustomHandler={onAccordionOpen}
              styles={`flex justify-between gap-8 px-0 md:px-10 ${index === activeIndex ? "pt-16 pb-0" : "py-16"} ${
                index !== activeIndex && index !== accordionItems.length - 1 ? "border-b border-thinLightGrey" : ""
              }`}
              arrowClasses="flex-shrink-0"
            >
              <Typography variant="p" Tag="span" classes="font-500 text-14 text-black-900 flex items-center gap-8">
                {getAccordionIcon(item.label)} {item.label}
              </Typography>
            </AccordionTitle>

            <AccordionContent
              index={index}
              activeIndex={activeIndex}
              styles={`${index === activeIndex ? "pt-12" : ""} ${
                index === activeIndex && index !== accordionItems.length - 1 ? "border-b border-thinLightGrey" : ""
              }`}
            >
              <div className="descriptionTitle">
                <div dangerouslySetInnerHTML={{ __html: item.html }} />
              </div>
            </AccordionContent>
          </React.Fragment>
        ))}
      </Accordion>
    </div>
  );
};

AccordionSection.propTypes = {
  currentItem: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default AccordionSection;
